import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { complianceRulesApi, regulationMonitoringApi, regulationReviewApi } from '../lib/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CalendarIcon,
  LinkIcon,
  BeakerIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type TabType = 'versions' | 'monitoring' | 'reviews';

interface RuleVersion {
  id: string;
  version: string;
  effectiveDate: string;
  deprecatedAt: string | null;
  isActive: boolean;
  changelog: string | null;
  source: string | null;
  createdAt: string;
  updatedAt: string;
  _count: {
    reports: number;
  };
}

interface RegulationDocument {
  documentNumber: string;
  title: string;
  publicationDate: string;
  url: string;
}

interface MonitoringCheck {
  checkedAt: string;
  fdicCount: number;
  ncuaCount: number;
  fdicDocuments: RegulationDocument[];
  ncuaDocuments: RegulationDocument[];
}

interface RegulationReview {
  id: string;
  documentNumber: string;
  title: string;
  publicationDate: string;
  agency: string;
  documentUrl: string;
  status: 'PENDING' | 'IN_REVIEW' | 'APPROVED' | 'IMPLEMENTED' | 'DISMISSED';
  summary: string | null;
  impact: string | null;
  suggestedChanges: string[] | null;
  reviewNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('versions');
  const [showVersionDetails, setShowVersionDetails] = useState<string | null>(null);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const queryClient = useQueryClient();

  // Fetch rule versions
  const { data: versionsData, isLoading: versionsLoading } = useQuery({
    queryKey: ['compliance-versions'],
    queryFn: async () => {
      const response = await complianceRulesApi.getVersions();
      return response.data;
    },
  });

  // Fetch monitoring history
  const { data: latestCheck, isLoading: monitoringLoading } = useQuery({
    queryKey: ['regulation-monitoring-latest'],
    queryFn: async () => {
      const response = await regulationMonitoringApi.getLatest();
      return response.data as MonitoringCheck;
    },
  });

  // Fetch resources
  const { data: resources } = useQuery({
    queryKey: ['regulation-resources'],
    queryFn: async () => {
      const response = await regulationMonitoringApi.getResources();
      return response.data;
    },
  });

  // Fetch regulation reviews
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['regulation-reviews'],
    queryFn: async () => {
      const response = await regulationReviewApi.getReviews({ status: 'PENDING' });
      return response.data;
    },
  });

  // Fetch pending count
  const { data: pendingCount } = useQuery({
    queryKey: ['regulation-reviews-pending-count'],
    queryFn: async () => {
      const response = await regulationReviewApi.getPendingCount();
      return response.data;
    },
  });

  // Activate version mutation
  const activateMutation = useMutation({
    mutationFn: (versionId: string) => complianceRulesApi.activateVersion(versionId),
    onSuccess: () => {
      toast.success('Rule version activated successfully');
      queryClient.invalidateQueries({ queryKey: ['compliance-versions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to activate version');
    },
  });

  // Check for updates mutation
  const checkUpdatesMutation = useMutation({
    mutationFn: (daysBack: number) => regulationMonitoringApi.checkUpdates(daysBack),
    onSuccess: (response) => {
      const data = response.data;
      if (data.totalCount > 0) {
        toast.success(`Found ${data.totalCount} new regulation(s)!`, { duration: 5000 });
      } else {
        toast.success('No new regulations found');
      }
      queryClient.invalidateQueries({ queryKey: ['regulation-monitoring-latest'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-reviews-pending-count'] });
      setCheckingUpdates(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to check for updates');
      setCheckingUpdates(false);
    },
  });

  // Analyze regulation mutation
  const analyzeMutation = useMutation({
    mutationFn: (reviewId: string) => regulationReviewApi.analyzeRegulation(reviewId),
    onSuccess: () => {
      toast.success('Analysis complete');
      queryClient.invalidateQueries({ queryKey: ['regulation-reviews'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to analyze regulation');
    },
  });

  // Dismiss regulation mutation
  const dismissMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      regulationReviewApi.dismiss(id, reason),
    onSuccess: () => {
      toast.success('Regulation dismissed');
      queryClient.invalidateQueries({ queryKey: ['regulation-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-reviews-pending-count'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to dismiss regulation');
    },
  });

  // Approve regulation mutation
  const approveMutation = useMutation({
    mutationFn: (reviewId: string) => regulationReviewApi.approve(reviewId),
    onSuccess: () => {
      toast.success('Regulation approved for implementation');
      queryClient.invalidateQueries({ queryKey: ['regulation-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['regulation-reviews-pending-count'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to approve regulation');
    },
  });

  const handleCheckUpdates = () => {
    setCheckingUpdates(true);
    checkUpdatesMutation.mutate(90); // Check last 90 days
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Compliance Administration</h1>
        <p className="text-gray-600 mt-2">
          Manage compliance rule versions and monitor regulatory updates
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('versions')}
            className={`${
              activeTab === 'versions'
                ? 'border-ocean-600 text-ocean-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Rule Versions
          </button>
          <button
            onClick={() => setActiveTab('monitoring')}
            className={`${
              activeTab === 'monitoring'
                ? 'border-ocean-600 text-ocean-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Regulation Monitoring
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`${
              activeTab === 'reviews'
                ? 'border-ocean-600 text-ocean-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors inline-flex items-center`}
          >
            Regulation Reviews
            {pendingCount?.count > 0 && (
              <span className="ml-2 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                {pendingCount.count}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Rule Versions Tab */}
      {activeTab === 'versions' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Compliance Rule Versions</h2>
            </div>

            {versionsLoading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-600"></div>
                <p className="text-gray-600 mt-4">Loading versions...</p>
              </div>
            ) : versionsData?.versions?.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {versionsData.versions.map((version: RuleVersion) => (
                  <div key={version.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {version.version}
                          </h3>
                          {version.isActive ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircleIcon className="w-4 h-4 mr-1" />
                              Active
                            </span>
                          ) : version.deprecatedAt ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Deprecated
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Inactive
                            </span>
                          )}
                        </div>

                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="w-4 h-4" />
                            <span>Effective since: {formatDate(version.effectiveDate)}</span>
                          </div>
                          {version.deprecatedAt && (
                            <div className="flex items-center space-x-2">
                              <ClockIcon className="w-4 h-4" />
                              <span>Deprecated on: {formatDate(version.deprecatedAt)}</span>
                            </div>
                          )}
                          <div className="flex items-center space-x-2">
                            <DocumentTextIcon className="w-4 h-4" />
                            <span>Used in {version._count.reports} report(s)</span>
                          </div>
                        </div>

                        {version.changelog && (
                          <p className="mt-2 text-sm text-gray-700">
                            <span className="font-medium">Changes:</span> {version.changelog}
                          </p>
                        )}

                        {version.source && (
                          <a
                            href={version.source}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center text-sm text-ocean-600 hover:text-ocean-700"
                          >
                            <LinkIcon className="w-4 h-4 mr-1" />
                            View Source
                          </a>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {!version.isActive && (
                          <button
                            onClick={() => activateMutation.mutate(version.id)}
                            disabled={activateMutation.isPending}
                            className="bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 disabled:opacity-50 transition-all text-sm"
                          >
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() =>
                            setShowVersionDetails(
                              showVersionDetails === version.id ? null : version.id
                            )
                          }
                          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-all text-sm"
                        >
                          {showVersionDetails === version.id ? 'Hide Details' : 'View Details'}
                        </button>
                      </div>
                    </div>

                    {showVersionDetails === version.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-xs text-gray-500 mb-2">
                          Created: {formatDateTime(version.createdAt)} | Updated:{' '}
                          {formatDateTime(version.updatedAt)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center text-gray-500">
                <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p>No compliance rule versions found</p>
                <p className="text-sm mt-2">Create your first version to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Regulation Monitoring Tab */}
      {activeTab === 'monitoring' && (
        <div className="space-y-6">
          {/* Check for Updates */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Check for Regulatory Updates</h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Manually check the Federal Register for new FDIC and NCUA advertising regulations
                from the last 90 days.
              </p>
              <button
                onClick={handleCheckUpdates}
                disabled={checkingUpdates}
                className="bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 disabled:opacity-50 transition-all inline-flex items-center"
              >
                <ArrowPathIcon className={`w-5 h-5 mr-2 ${checkingUpdates ? 'animate-spin' : ''}`} />
                {checkingUpdates ? 'Checking...' : 'Check for Updates'}
              </button>
            </div>
          </div>

          {/* Latest Check Results */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Latest Check Results</h2>
            </div>

            {monitoringLoading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-600"></div>
                <p className="text-gray-600 mt-4">Loading results...</p>
              </div>
            ) : latestCheck?.checkedAt ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600">
                    Last checked: {formatDateTime(latestCheck.checkedAt)}
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    Total: {latestCheck.fdicCount + latestCheck.ncuaCount} regulation(s) found
                  </div>
                </div>

                {latestCheck.fdicCount > 0 && (
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-amber-600" />
                      FDIC Updates ({latestCheck.fdicCount})
                    </h3>
                    <div className="space-y-3">
                      {latestCheck.fdicDocuments.map((doc) => (
                        <div
                          key={doc.documentNumber}
                          className="p-4 bg-amber-50 border border-amber-200 rounded-md"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">{doc.title}</h4>
                          <div className="text-sm text-gray-600 mb-2">
                            Published: {formatDate(doc.publicationDate)}
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-ocean-600 hover:text-ocean-700 inline-flex items-center"
                          >
                            <LinkIcon className="w-4 h-4 mr-1" />
                            View on FederalRegister.gov
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {latestCheck.ncuaCount > 0 && (
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-amber-600" />
                      NCUA Updates ({latestCheck.ncuaCount})
                    </h3>
                    <div className="space-y-3">
                      {latestCheck.ncuaDocuments.map((doc) => (
                        <div
                          key={doc.documentNumber}
                          className="p-4 bg-amber-50 border border-amber-200 rounded-md"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">{doc.title}</h4>
                          <div className="text-sm text-gray-600 mb-2">
                            Published: {formatDate(doc.publicationDate)}
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-ocean-600 hover:text-ocean-700 inline-flex items-center"
                          >
                            <LinkIcon className="w-4 h-4 mr-1" />
                            View on FederalRegister.gov
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {latestCheck.fdicCount === 0 && latestCheck.ncuaCount === 0 && (
                  <div className="p-8 text-center bg-green-50 border border-green-200 rounded-md">
                    <CheckCircleIcon className="w-12 h-12 mx-auto text-green-600 mb-3" />
                    <p className="text-green-800 font-medium">No new regulations found</p>
                    <p className="text-sm text-green-700 mt-1">
                      Your compliance rules are up to date
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-12 text-center text-gray-500">
                <ClockIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p>No monitoring checks have been run yet</p>
                <p className="text-sm mt-2">Click "Check for Updates" to run your first check</p>
              </div>
            )}
          </div>

          {/* Official Resources */}
          {resources && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold">Official Regulation Sources</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">FDIC Resources</h3>
                    <div className="space-y-2">
                      <a
                        href={resources.fdic.eCFR}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-ocean-600 hover:text-ocean-700"
                      >
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        Part 328 on eCFR
                      </a>
                      <a
                        href={resources.fdic.federalRegister}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-ocean-600 hover:text-ocean-700"
                      >
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        FDIC on Federal Register
                      </a>
                      <a
                        href={resources.fdic.officialGuidance}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-ocean-600 hover:text-ocean-700"
                      >
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        Official Guidance
                      </a>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">NCUA Resources</h3>
                    <div className="space-y-2">
                      <a
                        href={resources.ncua.eCFR}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-ocean-600 hover:text-ocean-700"
                      >
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        Part 740 on eCFR
                      </a>
                      <a
                        href={resources.ncua.federalRegister}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-ocean-600 hover:text-ocean-700"
                      >
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        NCUA on Federal Register
                      </a>
                      <a
                        href={resources.ncua.officialGuidance}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-ocean-600 hover:text-ocean-700"
                      >
                        <LinkIcon className="w-4 h-4 inline mr-1" />
                        Official Guidance
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Regulation Reviews Tab */}
      {activeTab === 'reviews' && (
        <div className="space-y-6">
          {reviewsLoading ? (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-600"></div>
              <p className="text-gray-600 mt-4">Loading reviews...</p>
            </div>
          ) : reviewsData?.reviews?.length > 0 ? (
            <div className="space-y-4">
              {reviewsData.reviews.map((review: RegulationReview) => (
                <div key={review.id} className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              review.agency === 'FDIC'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-teal-100 text-teal-800'
                            }`}
                          >
                            {review.agency}
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              review.status === 'PENDING'
                                ? 'bg-yellow-100 text-yellow-800'
                                : review.status === 'IN_REVIEW'
                                ? 'bg-blue-100 text-blue-800'
                                : review.status === 'APPROVED'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {review.status.replace('_', ' ')}
                          </span>
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{review.title}</h3>

                        <div className="space-y-1 text-sm text-gray-600 mb-3">
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="w-4 h-4" />
                            <span>Published: {formatDate(review.publicationDate)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <DocumentTextIcon className="w-4 h-4" />
                            <span>Document: {review.documentNumber}</span>
                          </div>
                        </div>

                        {review.summary && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Summary:</p>
                            <p className="text-sm text-gray-600">{review.summary}</p>
                          </div>
                        )}

                        {review.impact && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Impact:</p>
                            <p className="text-sm text-gray-600">{review.impact}</p>
                          </div>
                        )}

                        {review.suggestedChanges && review.suggestedChanges.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">
                              Suggested Changes:
                            </p>
                            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                              {review.suggestedChanges.map((change, idx) => (
                                <li key={idx}>{change}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <a
                          href={review.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-ocean-600 hover:text-ocean-700"
                        >
                          <LinkIcon className="w-4 h-4 mr-1" />
                          View Document
                        </a>
                      </div>

                      <div className="flex flex-col space-y-2 ml-4">
                        {review.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => analyzeMutation.mutate(review.id)}
                              disabled={analyzeMutation.isPending}
                              className="bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 disabled:opacity-50 transition-all text-sm inline-flex items-center justify-center"
                            >
                              <BeakerIcon className="w-4 h-4 mr-1" />
                              Analyze
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt(
                                  'Why are you dismissing this regulation? (required)'
                                );
                                if (reason) {
                                  dismissMutation.mutate({ id: review.id, reason });
                                }
                              }}
                              disabled={dismissMutation.isPending}
                              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-all text-sm inline-flex items-center justify-center"
                            >
                              <XMarkIcon className="w-4 h-4 mr-1" />
                              Dismiss
                            </button>
                          </>
                        )}

                        {review.status === 'IN_REVIEW' && (
                          <>
                            <button
                              onClick={() => approveMutation.mutate(review.id)}
                              disabled={approveMutation.isPending}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 transition-all text-sm inline-flex items-center justify-center"
                            >
                              <CheckCircleIcon className="w-4 h-4 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt(
                                  'Why are you dismissing this regulation? (required)'
                                );
                                if (reason) {
                                  dismissMutation.mutate({ id: review.id, reason });
                                }
                              }}
                              disabled={dismissMutation.isPending}
                              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-all text-sm inline-flex items-center justify-center"
                            >
                              <XMarkIcon className="w-4 h-4 mr-1" />
                              Dismiss
                            </button>
                          </>
                        )}

                        {review.status === 'APPROVED' && (
                          <div className="text-sm text-green-700 font-medium">
                            Ready for implementation
                          </div>
                        )}
                      </div>
                    </div>

                    {review.reviewNotes && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-1">Admin Notes:</p>
                        <p className="text-sm text-gray-600">{review.reviewNotes}</p>
                        {review.reviewedBy && (
                          <p className="text-xs text-gray-500 mt-1">
                            Reviewed by {review.reviewedBy} on {formatDateTime(review.reviewedAt!)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-12 text-center text-gray-500">
              <CheckCircleIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p>No pending regulation reviews</p>
              <p className="text-sm mt-2">
                Run "Check for Updates" to find new regulations
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
